import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiErrorResponse } from "../models/ApiResponse";
import { isUndefinedOrTrue } from "../utils/object.utils";
import { apiStore } from "../stores/api.store";

export interface ProRatingInfo {
	startDate: string;
	daysInMonth: number;
	remainingDays: number;
	workingDays: number;
	formula: string;
}

export interface GuaranteedHoursProgress {
	consultantId: string;
	month: string;
	guaranteedHours: number;
	actualHours: number;
	remainingHours: number;
	isProRated?: boolean;
	proRatingInfo?: ProRatingInfo;
	consultantGuaranteedHoursPerMonth?: number;
	collectiveAgreementGuaranteedHoursPerMonth?: number;
}

export interface ConsultantWithEmployment {
	id: string;
	firstName: string;
	lastName: string;
	employeeNumber?: number;
	employment?: {
		started?: Date | string;
		ended?: Date | string;
	};
}

export interface ConsultantsByCollectiveAgreementResponse {
	count: number;
	consultantIds: string[];
	consultants: ConsultantWithEmployment[];
}

/**
 * Return the consultants guaranteed hours (garantilön).
 *
 * This is part of collective agreements, where some has a guaranteed number of
 * hours the consultans get paid for each month and it is up to the organisation.
 *
 * @param consultantId
 * @param month
 * @param enabled
 * @returns
 */
export function useConsultantGuaranteedHours(
	consultantId?: string,
	month?: string,
	enabled?: boolean
) {
	const { data, ...rest } = useQuery(
		["consultant-guaranteed-hours", consultantId, month],
		() =>
			apiStore
				.get<GuaranteedHoursProgress>({
					path: `/consultant/${consultantId}/guaranteed-hours`,
					isAuthRequired: true,
					query: month ? { month } : {},
				})
				.then((d) => d.data),
		{
			enabled: !!consultantId && isUndefinedOrTrue(enabled),
			staleTime: 5 * 60 * 1000, // 5 minutes
		}
	);

	return {
		guaranteedHoursProgress: data,
		...rest,
	};
}

/**
 * Get count and IDs of consultants using a specific collective agreement.
 * Used to show admin how many consultants will be affected by bulk updates.
 *
 * @param collectiveAgreementId
 * @param enabled
 * @returns
 */
export function useConsultantsByCollectiveAgreement(
	collectiveAgreementId?: string,
	enabled?: boolean
) {
	const { data, ...rest } = useQuery(
		["consultants-by-collective-agreement", collectiveAgreementId],
		() =>
			apiStore
				.post<ConsultantsByCollectiveAgreementResponse>({
					path: `/collective-agreement/by-consultants`,
					body: { collectiveAgreementId },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			enabled: !!collectiveAgreementId && isUndefinedOrTrue(enabled),
			staleTime: 2 * 60 * 1000, // 2 minutes
		}
	);

	return {
		data,
		...rest,
	};
}
